import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import Layout from 'layouts/Admin';
import { useToast } from '@chakra-ui/react';
import { IDocumentPostMetadata, ISigner } from '@cincel/client';
import {
  Button,
  Checkbox,
  TabsPanel,
  PanelDataType,
  CertificateType,
  PopOver,
  Select,
  TextInput,
} from '@design-system';
import fp from 'lodash/fp';
import { NoCreditModal } from './components/NoCreditModal';
import { NoPdfFileModal } from './components/NoPdfFileModal';
import { NewStageModal } from './components/NewStageModal';
import { SuccessModal } from './components/SuccessModal';
import { SignerModal } from './components/SignerModal';
import { StepsHeader } from './components/StepsHeader';
import { useRouter } from 'hooks/useRouter';
import { useSession } from 'store/session';
import { useToggle } from 'hooks/useToggle';
import { hasAvailableKyc } from './utils/hasAvailableKyc';
import { generateRandomHex } from './utils/generateRandomHex';
import { ExistingAnnotationsModal } from './components/ExistingAnnotationsModal';
import { WLContext } from '@contexts';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useSentry } from 'hooks/useSentry';
import { useMixpanel } from 'hooks/useMixpanel';
import { SignersTab } from './components/SignersTab';
import { ObserversTab, ObserverType } from './components/ObserversTab';
import { usePost as useCreateContact } from '@pages/ContactsPage/hooks';
import { useFetchTeamFolders } from 'hooks/useFetchTeamFolders';
import { usePost } from './hooks/usePost';
import { ERROR_TOAST_DEFAULTS } from '@helpers';
import { useWindowSize } from 'hooks/useWindowSize';
import { PdfViewport } from './components/PdfViewport';
import { ErrorModal } from './components/ErrorModal';
import useGetFeatures from './hooks/use-get-features';
import { getStorage, getSessionUser } from 'helpers/sessionContext';


interface Errortype {
  type: string;
  name: 'folder' | 'name';
  message: string;
}

export enum DocumentSteps {
  create = 'create',
  share = 'share',
  sign = 'sign',
}

const NewDocument = () => {
  const { addBreadcrumb, captureException } = useSentry();
  const { trackEvent } = useMixpanel();
  const { t } = useTranslation();

  const { color } = useContext(WLContext);

  const { history } = useRouter();
  const [isOpen, toggle] = useToggle();
  const [errorModal, toggleErrorModal] = useToggle();
  const toast = useToast();

  const { currentOrganization, team, credits } = useSession();

  const { data: teamFolders, isLoading: isTeamFoldersLoading } =
    useFetchTeamFolders({}, { pageSize: 100000 });
  const { mutateAsync: createDocument, isLoading: isCreatingDocumentLoading } =
    usePost({});

  const { data: features } = useGetFeatures({ team: team?.uuid });

  const tmpFile = useRef();
  const stepsRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const widthRef = useRef(0);

  const { width: windowWidth, height } = useWindowSize();
  const topSpacing = 196;
  const isDesktop = windowWidth >= 992;

  const [showModal, setShowModal] = useState('');
  const [convertFileModal, setCovertFileModal] = useState(false);
  const [existingAnnotationModal, setExistingAnnotationModal] = useState(false);
  const [isNoCreditModalOpen, setIsNoCreditModalOpen] = useState(
    credits?.docs?.spent >= credits?.docs?.got,
  );
  const [user, setUser] = useState<any>({});
  const [stages, setStages] = useState<any>([]);
  const [stageName, setStageName] = useState('');
  const [currentStage, setCurrentStage] = useState(1);
  const [currentSignerIndex, setCurrentSignerIndex] = useState(0);
  const [file, setFile] = useState<File>();
  const [currentStep, setCurrentStep] = useState(DocumentSteps.create);
  const [signerData, setSignerData] = useState<any>();
  const [isEditingSigner, setIsEditingSigner] = useState(false);
  const [formErros, setFormErrors] = useState<Array<Errortype>>([]);
  const [documentID, setDocumentID] = useState<number | null>(null);
  const [width, setWidth] = useState(0);
  const [observers, setObservers] = useState<ObserverType[]>([]);
  const [contactsList, setContactsList] = useState([]);
  const [addSignerToAgenda, setAddSignerToAgenda] = useState(false);
  const [signatureAdded, setSignatureAdded] = useState(false);
  const { mutateAsync: onCreateContact } = useCreateContact();

  const [settings, setSettings] = React.useState<SettingValues>({
    folder: '',
    document_name: '',
    document_description: '',
    self_sign: true,
    notify_me: false,
    show_certificate: true,
  });

  const div = useCallback((node) => {
    if (node !== null) {
      setWidth(node.getBoundingClientRect().width);
      widthRef.current = node.getBoundingClientRect().width;
    }
  }, []);

  const hideConvertFileModal = () => {
    setCovertFileModal(false);
  };
  const hideExistingAnnotationsModal = () => {
    setExistingAnnotationModal(false);
  };

  const handleExistingAnnotationsCancel = async () => {
    setExistingAnnotationModal(false);
  };

  const convertModalSuccess = (active) => {
    handleConvertPdfModal(active);
    setFile(tmpFile.current);
  };

  const existingAnnotationSuccess = () => {
    // TODO: handle existing annotations
  };

  const handleStepCancel = () => {
    setFile(undefined);
    tmpFile.current = undefined;
  };

  const handleDocumentResponse = async (response) => {
    if (response.status === 201) {
      setDocumentID(response.data.uuid);
      toggle();
    }
  };

  const handleSignRedirect = useCallback(
    () =>
      history.push(
        `/teams/${team?.uuid}/folders/${settings?.folder}/document/${documentID}`,
      ),
    [documentID, history],
  );

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  const validateRfc = (rfc) => {
    const moralRegex = /^[A-Z&Ñ]{3}\d{6}[A-Z0-9]{3}$/;
    const fisicaRegex = /^[A-Z&Ñ]{4}\d{6}[A-Z0-9]{3}$/;

    const value = String(rfc ?? '').toUpperCase();

    return moralRegex.test(value) || fisicaRegex.test(value);
  };

  const handleCreateStep = () => {
    const errorHasForms: Array<Errortype> = [];
    Object.entries(settings).forEach(([key, value]) => {
      if (key === 'document_name' && value === '') {
        errorHasForms.push({
          type: 'required',
          name: 'name',
          message: t(translations.ERRORS.DOCUMENT_NAME_REQUIRED),
        });
      }
      if (key === 'folder' && (value === '' || value === null)) {
        errorHasForms.push({
          type: 'required',
          name: 'folder',
          message: t(translations.ERRORS.DOCUMENT_FOLDER_REQUIRED),
        });
      }
    });

    setFormErrors(errorHasForms);
    if (errorHasForms.length > 0) return;

    // Go to top to the screen to avoid confusions

    if (settings.self_sign) {
      const isCreatorOnStage = stages.find((stage) =>
        stage.signers.filter((signer) => signer.email === user.email),
      );

      if (typeof isCreatorOnStage === 'undefined') {
        setStages([
          ...stages,
          {
            signers: [
              {
                email: user.email,
                name: user.name,
                stage: 0,
                validate_rfc: false,
                identity_verification: false,
                user_coordinates: false,
                canLifeProof: hasAvailableKyc(),
                hex: color,
                autograph: true,
                advanced: true,
                typed: true,
                notification: settings.notify_me,
              },
            ],
          } as any,
        ]);
      }
    }
    setCurrentStep(DocumentSteps.share);
  };

  const getAnnotationCoordinates = (annotations) => {
    const coordinates = annotations[0].signatures.map(
      ({ page, x, y, w, h }) => ({
        page,
        x,
        y,
        w,
        h,
      }),
    );
    return coordinates;
  };

  const handleCreateDocument = async ({
    signers: signersAnnotations,
    file: pdfFile,
  }) => {
    const allSigners = stages.map((el) => [...el.signers]).flat();
    const signersWithAnnotations = signersAnnotations.filter(
      (s) => s.signatures.length > 0,
    );

    let canSign = false;
    if (signersWithAnnotations?.length === 0 && allSigners.length > 0)
      canSign = true;
    if (signersWithAnnotations?.length === allSigners.length) canSign = true;

    if (!canSign) {
      toggleErrorModal();
      return;
    }

    const folder = settings.folder;

    const signers: ISigner[] = stages
      .map((el) => [...el.signers])
      .flat()
      .map((signer) => {
        const signatureAnnotations = signersAnnotations.filter(
          (annotation) => annotation.id === signer.email,
        );

        const signerData: ISigner = {
          name: signer.name,
          email: signer.email,
          stage: signer.stage + 1,
          identity_verification: signer.identity_verification,
          autograph: Boolean(signer.autograph),
          advanced: Boolean(signer.advanced),
          typed: Boolean(signer.typed),
          notify_creator: Boolean(signer.notification),
          // @ts-ignore
          user_coordinates: !!signer.user_coordinates,
          validate_rfc: !!signer.validate_rfc,
          rfc: signer.validate_rfc === true ? signer.rfc : undefined,
        };

        if (
          signatureAnnotations.length &&
          signatureAnnotations[0].signatures.length
        ) {
          signerData.signature_coordinates =
            getAnnotationCoordinates(signatureAnnotations);
        }

        return signerData;
      });

    const observersInvitations = observers.map((observer) => ({
      name: observer.name,
      email: observer.email,
    }));

    const getSignatureTypes = ({ autograph, advanced, typed }: ISigner) => {
      const types: string[] = [];

      if (autograph) types.push('autograph');
      if (advanced) types.push('advanced');
      if (typed) types.push('name'); // Backend expects 'name' for typed signatures

      return types.join(',');
    };

    // build pdf
    const customHeaders: IDocumentPostMetadata = {
      metadata: {
        name: String(settings.document_name).normalize('NFKC'),
        description: String(settings.document_description).normalize('NFKC'),
        notify_creator: settings.notify_me,
        hide_certificate_pii: !settings.show_certificate,
      },
      observers: observersInvitations,
      // TODO: Remove this map, this is only to not send the new headers
      signers: signers.map((signer) => ({
        email: signer.email,
        identity_verification: signer.identity_verification,
        name: signer.name,
        stage: signer.stage,
        signature_coordinates: signer?.signature_coordinates,
        signature_allowed_types: getSignatureTypes(signer),
        signature_notify_creator: signer?.notify_creator,
        // @ts-ignore
        user_coordinates: signer?.user_coordinates,
        rfc: signer.validate_rfc === true ? signer.rfc : undefined,
      })),
      folder,
    };

    try {
      addBreadcrumb(
        'create_document',
        `User ${user.email} with organization ${(currentOrganization as any).id
        }`,
      );

      const pdf = new File([pdfFile], settings.document_name, {
        type: 'application/pdf',
      });

      const documentResponse = await createDocument({
        ...customHeaders,
        document: pdf,
      });

      trackEvent('Document created', {
        email: user.email,
        orgnizationId: (currentOrganization as any).id,
        ...customHeaders,
        base64_file: undefined,
      });

      handleDocumentResponse(documentResponse);
      return;
    } catch (err) {
      captureException(new Error('Document could not be created'), {
        extra: {
          ...customHeaders,
          base64_file: undefined,
        },
      });
      console.error(err);
    }
  };

  // handle change step
  const handleStep = async (step, data?) => {
    if (currentStep === DocumentSteps.create && step === DocumentSteps.create) {
      handleCreateStep();
    } else if (step === DocumentSteps.share) {
      handleCreateDocument(data);
    } else if (
      step === DocumentSteps.create &&
      currentStep === DocumentSteps.share
    ) {
      setCurrentStep(step);
    }
  };

  const handleAddStages = () => {
    setStages([...stages, { signers: [] }]);
  };

  const handleAddSigner = (stage) => {
    setShowModal('signer');
    setSignerData({
      identity_verification: false,
      email: '',
      name: '',
      validate_rfc: false,
      canLifeProof: hasAvailableKyc(),
      autograph: true,
      advanced: true,
      notification: false,
      typed: true,
      user_coordinates: false,
    });
    setCurrentStage(stage);
  };

  const handleEditSignerSuccess = (cb) => {
    const allSigners = stages
      .map((stage) => [...stage.signers])
      .flat()
      .filter((signer) => signer.email !== signerData.email);

    const emailExists = stages
      .map((stage) => [...stage.signers])
      .flat()
      .find((signer) => signer.email === signerData.email);

    if (
      emailExists &&
      emailExists.email === signerData.email &&
      emailExists.hex !== signerData.hex
    ) {
      toast({
        ...ERROR_TOAST_DEFAULTS,
        title: t(translations.ERRORS.EXISTING_EMAIL),
      });
      return;
    }

    cb();

    const newStages = [...stages];
    newStages[currentStage].signers[currentSignerIndex] = {
      ...signerData,
      canLifeProof: hasAvailableKyc(),
      stage: currentStage,
    };
    setStages([...newStages]);
    setIsEditingSigner(false);
    setSignerData({});
    setShowModal('');
  };

  const handleSignerSuccess = (cb) => {
    if (typeof signerData.email === 'undefined') return;

    const validation = validateEmail(signerData.email);

    if (validation === null) {
      return;
    }

    cb();

    const emailExists = stages
      .map((stage) => [...stage.signers])
      .flat()
      .find((signer) => signer.email === signerData.email);

    if (emailExists) {
      toast({
        ...ERROR_TOAST_DEFAULTS,
        title: t(translations.ERRORS.EXISTING_EMAIL),
      });
      return;
    }

    const s = stages;

    s[currentStage] = {
      ...s[currentStage],
      signers: [
        ...s[currentStage].signers,
        {
          ...signerData,
          stage: currentStage,
          hex: generateRandomHex(),
        },
      ] as any,
    };
    if (addSignerToAgenda) {
      onCreateContact({
        email: signerData?.email,
        name: signerData?.name,
      });
    }

    setStages(s);
    setCurrentStage(0);
    setSignerData({});
    setShowModal('');
    setAddSignerToAgenda(false);
  };

  const handleContactsSignerSuccess = (data) => {
    for (const contact of data) {
      const contactEmail = contact.email;
      const emailExists = stages
        .map((stage) => [...stage.signers])
        .flat()
        .find((signer) => signer.email === contactEmail);

      if (emailExists) {
        toast({
          ...ERROR_TOAST_DEFAULTS,
          title: t(translations.ERRORS.EXISTING_EMAIL),
        });
        return;
      }
    }

    const s = stages;

    s[currentStage] = {
      ...s[currentStage],
      signers: [
        ...s[currentStage].signers,
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...data?.map((item) => ({
          ...signerData,
          ...item,
          stage: currentStage,
          hex: generateRandomHex(),
        })),
      ] as any,
    };

    setStages(s);
    setContactsList([]);
    setShowModal('');
  };

  const handleConvertPdfModal = (active) => {
    if (active)
      getStorage().setItem('cincel-modal-convertDocument', '1');
  };

  const handleStageSuccess = () => {
    setStages([...stages, { signers: [] }]);
    setStageName('');
  };

  const handleDeleteStage = (index) => {
    const stageIndex = index;
    const newStages = [...stages];
    newStages.splice(stageIndex, 1);
    setStages([...newStages]);
  };

  const handleColorChange = (stage, index) => {
    const signerIndex = index;
    const newStages = [...stages];
    const generatedHex = generateRandomHex();
    newStages[stage].signers[signerIndex].hex = generatedHex;

    // TODO: handle color change

    setStages([...newStages]);
  };

  const handleDeleteSigner = (stage, index) => {
    const signerIndex = index;
    const newStages = [...stages];
    // TODO: handle delete signer

    newStages[stage].signers.splice(signerIndex, 1);
    setStages([...newStages]);
  };

  const handleEditSigner = (stage, index) => {
    const signerToEdit = stages[stage].signers[index];
    setShowModal('signer');
    setIsEditingSigner(true);
    setCurrentStage(stage);
    setCurrentSignerIndex(index);
    setSignerData({
      identity_verification: signerToEdit.identity_verification,
      email: signerToEdit.email,
      name: signerToEdit.name,
      hex: signerToEdit.hex,
      autograph: signerToEdit.autograph,
      advanced: signerToEdit.advanced,
      typed: signerToEdit.typed || false,
      notification: signerToEdit.notification,
      user_coordinates: signerToEdit.user_coordinates,
      validate_rfc: signerToEdit.validate_rfc,
      rfc: signerToEdit.rfc,
    });
  };

  const handleBuildSigner = (key, value) => {
    setSignerData({
      ...signerData,
      [key]: value,
    });
  };

  const isAddSignerDisabled = () => {
    if (
      typeof signerData?.name === 'undefined' ||
      typeof signerData?.email === 'undefined' ||
      !signerData?.name?.length ||
      !signerData?.email.length ||
      validateEmail(signerData?.email) === null ||
      (signerData?.validate_rfc === true &&
        validateRfc(signerData?.rfc) === false)
    ) {
      if (!fp.isEmpty(contactsList)) return false;
      return true;
    }
    return false;
  };

  /**
   * Set user session
   */
  useEffect(() => {
    const userSession = getSessionUser();
    setUser(userSession);
  }, []);

  useEffect(() => {
    if (stepsRef.current) {
      (stepsRef.current as HTMLElement).scrollIntoView();
    }
  }, [currentStep]);

  useEffect(() => {
    if (signatureAdded) {
      setSignatureAdded(false);
    }
  }, [signatureAdded]);

  const isKycEnabled = () => {
    return true;
  };

  const handleObservers = (data: ObserverType | ObserverType[]) => {
    if (Array.isArray(data)) {
      setObservers(data);
    } else {
      const oldObservers = [...observers, { ...data }];
      setObservers(oldObservers);
    }
  };

  const handleFile = (file: File) => {
    setFile(file);
  };

  const panels: PanelDataType[] = [
    {
      id: '1',
      key: 'tabs-signers-tab',
      name: t(translations.COMMON.SIGNERS),
      target: 'tabs-signers',
      isActive: true,
      component: (
        <SignersTab
          color={color}
          stages={stages}
          teamFeatures={features}
          deleteStage={handleDeleteStage}
          onClickNewStage={handleAddStages}
          onClickNewSigner={handleAddSigner}
          handleDeleteSigner={handleDeleteSigner}
          handleEditSigner={handleEditSigner}
          handleColorChange={handleColorChange}
        />
      ),
    },
    {
      id: '2',
      key: 'tabs-observers-tab',
      name: t(translations.COMMON.OBSERVERS),
      target: 'tabs-observers',
      isActive: false,
      component: (
        <ObserversTab
          color={color}
          setObservers={handleObservers}
          observers={observers}
        />
      ),
    },
  ];

  const totalHeight = height - topSpacing - 60;

  const isCreateDisabled = !file || !stages.length;

  const getDisabledReasons = () => {
    if (!file) {
      return translations.DOCUMENT_PAGES.POPOVER_FILE;
    }
    if (!stages.length) {
      return translations.DOCUMENT_PAGES.POPOVER_STAGES;
    }
    return 'Error inesperado.';
  };

  const isContinueDisabled = file ? false : true;

  return (
    <Layout>
      <div ref={stepsRef} className="scroll-mt-5">
        <StepsHeader primaryColor={color} currentStep={currentStep} />
      </div>

      <div className="createDocumentContainer container mx-auto px-0 py-8 pt-0 md:px-2">
        <div className="flex flex-wrap md:min-h-[600px]">
          {/* LEFT */}
          <div className="w-full px-4 md:px-0 lg:w-4/12 xl:w-4/12">
            {currentStep === DocumentSteps.create && (
              <>
                <Settings
                  errors={formErros}
                  folders={teamFolders?.data.map((folder) => ({
                    value: folder.uuid,
                    label: folder.name,
                  }))}
                  features={features}
                  onChange={(commit) => {
                    setSettings((prev) => ({
                      ...prev,
                      ...commit,
                    }));
                  }}
                  values={settings}
                />
                <div className="mt-4">
                  <CertificateType
                    color={color}
                    totalSigned={credits?.docs?.spent}
                    totalCount={credits?.docs?.got}
                  />
                </div>
              </>
            )}

            {currentStep === DocumentSteps.share && (
              <div
                className="share-document overflow-x-hidden overflow-y-scroll "
                style={{
                  maxHeight: isDesktop ? totalHeight : 600,
                }}
              >
                <TabsPanel color={color} panels={panels} />
              </div>
            )}
          </div>
          {/* Right */}
          <div
            ref={div}
            className="max-w-screen w-full p-0 px-2 md:px-4 lg:w-8/12 xl:w-8/12"
            style={{
              ...(isDesktop ? { height: totalHeight } : {}),
            }}
          >
            {typeof file !== 'undefined' && (
              <div className="mb-4 mt-6 text-left text-paragraph font-bold text-gray-500 md:mt-0">
                {settings.document_name?.length > 3
                  ? settings.document_name
                  : t(translations.DOCUMENT_PAGES.CREATE_DOCUMENT_NO_NAME)}
              </div>
            )}

            <PdfViewport
              isCreating
              parentHeight={totalHeight}
              primaryColor={color}
              isSignatureToolEnabled={
                stages.map((el) => [...el.signers]).flat().length > 0
              }
              signers={stages.map((el) => [...el.signers]).flat()}
              observers={observers}
              onFileAdded={handleFile}
              currentStep={currentStep}
              isButtonDisabled={isCreateDisabled}
              isLoading={isCreatingDocumentLoading}
              handleStep={handleStep}
              disabledReason={getDisabledReasons()}
            />
            <div className="mb-12 mt-3 flex md:mb-0 md:h-auto">
              {currentStep === DocumentSteps.create && (
                <>
                  <div className="inline-block">
                    <Button
                      className="max-w mb-2 px-8 text-white"
                      type="submit"
                      variant="solid"
                      color="#C4C4C4"
                      size="sm"
                      onClick={() => handleStepCancel()}
                    >
                      {t(translations.COMMON.CANCEL)}
                    </Button>
                  </div>
                  <div className="ml-auto inline-block">
                    {isContinueDisabled ? (
                      <PopOver
                        position="top"
                        content={
                          <>
                            <p>{t(getDisabledReasons())}</p>
                          </>
                        }
                      >
                        <Button
                          color={color}
                          className="max-w mb-2 mr-8 px-8"
                          isDisabled={true}
                          type="submit"
                          size="sm"
                        >
                          {t(translations.COMMON.CONTINUE)}
                        </Button>
                      </PopOver>
                    ) : (
                      <Button
                        color={color}
                        className="max-w mb-2 mr-8 px-8"
                        isDisabled={isContinueDisabled}
                        type="submit"
                        onClick={() => handleStep(DocumentSteps.create)}
                        isLoading={isCreatingDocumentLoading}
                        size="sm"
                      >
                        {t(translations.COMMON.CONTINUE)}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODALS */}
      <ExistingAnnotationsModal
        isOpen={existingAnnotationModal}
        onSuccess={existingAnnotationSuccess}
        setShowModal={hideExistingAnnotationsModal}
        onCancel={handleExistingAnnotationsCancel}
      />

      <NoPdfFileModal
        isOpen={convertFileModal}
        onSuccess={convertModalSuccess}
        setShowModal={hideConvertFileModal}
      />

      <NewStageModal
        isOpen={showModal === 'stage'}
        onSuccess={handleStageSuccess}
        setShowModal={() => setShowModal('')}
        stageName={stageName}
        setStageName={setStageName}
      />

      <SignerModal
        setShowModal={() => setShowModal('')}
        isOpen={showModal === 'signer'}
        isEditingSigner={isEditingSigner}
        handleEditSignerSuccess={handleEditSignerSuccess}
        handleSignerSuccess={handleSignerSuccess}
        setIsEditingSigner={setIsEditingSigner}
        isAddSignerDisabled={isAddSignerDisabled}
        signerData={signerData}
        handleBuildSigner={handleBuildSigner}
        hasAvailableKyc={hasAvailableKyc}
        isKycEnabled={isKycEnabled}
        contactsList={contactsList}
        setContactsList={setContactsList}
        handleContactsSignerSuccess={handleContactsSignerSuccess}
        addSignerToAgenda={addSignerToAgenda}
        setaddSignerToAgenda={setAddSignerToAgenda}
        features={features}
      />

      <SuccessModal
        primaryColor={color}
        handleSignRedirect={handleSignRedirect}
        isOpen={isOpen}
      />
      <ErrorModal
        handleClose={() => toggleErrorModal()}
        isOpen={errorModal}
        primaryColor={color}
      />

      <NoCreditModal
        isOpen={isNoCreditModalOpen}
        setShowModal={() => setIsNoCreditModalOpen(false)}
      />
    </Layout>
  );
};

type FolderOption = {
  value: string;
  label: string;
};

type SettingValues = {
  folder: string;
  document_name: string;
  document_description: string;
  self_sign: boolean;
  notify_me: boolean;
  show_certificate: boolean;
};

type SettingsProps = {
  folders?: FolderOption[];
  values: SettingValues;
  onChange: (values: Partial<SettingValues>) => void;
  features: Record<string, any>;
  errors: any;
};

function Settings(props: SettingsProps) {
  const { folders, values, onChange, features, errors } = props;
  const { t } = useTranslation();
  const { color } = useContext(WLContext);

  return (
    <div>
      <div className="mb-4 text-left text-paragraph font-bold text-gray-500">
        {t(translations.DOCUMENT_PAGES.CREATE_DOCUMENT_TITLE)}
      </div>

      <div className="space-y-4">
        {Array.isArray(folders) && folders.length ? (
          <Select
            options={folders}
            name="folder"
            className="w-full"
            placeholder={t(translations.INPUTS.SELECT_FOLDER)}
            colorWL={color}
            value={folders.find((folder) => folder.value === values.folder)}
            onChange={(option: string) => {
              onChange({
                folder: option,
              });
            }}
          ></Select>
        ) : null}
        <div>
          <TextInput
            className="w-12/12 bg-white"
            name="document_name"
            isFullWidth
            required
            placeholder={t(translations.INPUTS.DOCUMENT_NAME)}
            value={values.document_name}
            onChange={(event) => {
              onChange({ [event.target.name]: event.target.value });
            }}
          />
        </div>
        <div>
          <TextInput
            className="w-12/12 bg-white"
            name="document_description"
            isFullWidth
            placeholder={t(translations.COMMON.DESCRIPTION)}
            value={values.document_name}
            onChange={(event) => {
              onChange({ [event.target.name]: event.target.value });
            }}
          />
        </div>
        <div>
          <Checkbox
            defaultValue={values.self_sign}
            onChange={(next: boolean) => onChange({ self_sign: next })}
            label={t(translations.INPUTS.DOCUMENT_CHECKBOX)}
          />
        </div>
        <div>
          <Checkbox
            defaultValue={values.notify_me}
            onChange={(next: boolean) => onChange({ notify_me: next })}
            label={t(translations.INPUTS.DOCUMENT_NOTIFICATION)}
          />
        </div>
        {!!features?.can_hide_certificate_pii?.enabled && (
          <Checkbox
            defaultValue={values.show_certificate}
            onChange={(next: boolean) => onChange({ show_certificate: next })}
            label={t(translations.INPUTS.SHOW_CERTIFICATE_PII)}
          />
        )}
      </div>
    </div>
  );
}

export default NewDocument;
